import { Badge, Box, Flex, HStack, Text } from "@chakra-ui/react";
import { ArrowUpRight, UserGear, UsersFour } from "@phosphor-icons/react";
import { getProgressColor, getProgressStyle, progressOf } from "utils/stageProgress";

/**
 * One workstream-year as a card.
 *
 * Two shapes from one component, switched by `prominent`: the current year gets the description, the owner and
 * assignee chips and a thick bar; older years get the title, stage and a thin bar. The same split v1 uses, so
 * the two modules' list pages read alike.
 */
export default function QuestionYearCard({ question, prominent, onOpen }) {
  return (
    <Box
      borderWidth="1px"
      borderColor={prominent ? "lime" : "gray.200"}
      borderRadius="xl"
      p={6}
      bg={prominent ? "gray.50" : "white"}
      boxShadow="sm"
      cursor="pointer"
      h="100%"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      _hover={{ borderColor: prominent ? "lime" : "gray.300", shadow: "md" }}
      onClick={onOpen}
    >
      <Flex justify="space-between" align="flex-start">
        <Box>
          <Text fontSize={prominent ? "lg" : "md"} fontWeight="bold" color="gray.800">
            {question.question}
          </Text>
          {/* The question's OWN description, set from its type server-side — not a per-section constant
              repeated on the client, which would drift from what the answer page calls the same question. */}
          {prominent && question.description ? (
            <Text fontSize="sm" color="gray.600" mt={1}>
              {question.description}
            </Text>
          ) : null}
        </Box>
        <Box color={prominent ? "gray.500" : "gray.400"} flexShrink={0} ml={4}>
          <ArrowUpRight size={20} />
        </Box>
      </Flex>

      {prominent ? (
        <Flex direction="column" gap={2} mt={4}>
          <PeopleRow icon={UserGear} label="Owner">
            {question.owner ? (
              <Chip colorScheme="gray">{question.owner}</Chip>
            ) : (
              <NotAssigned />
            )}
          </PeopleRow>
          <PeopleRow icon={UsersFour} label="Assignees" align="flex-start">
            {question.assignees?.length > 0 ? (
              <HStack spacing={2} flexWrap="wrap">
                {question.assignees.map((assignee) => (
                  <Chip key={assignee} colorScheme="green">
                    {assignee}
                  </Chip>
                ))}
              </HStack>
            ) : (
              <NotAssigned />
            )}
          </PeopleRow>
        </Flex>
      ) : null}

      <Box mt={4}>
        <StageBar question={question} large={prominent} />
      </Box>
    </Box>
  );
}

function PeopleRow({ icon: Icon, label, align = "center", children }) {
  return (
    <HStack spacing={2} align={align}>
      <HStack spacing={1} minW="80px" mt={align === "flex-start" ? 0.5 : 0}>
        <Icon size={14} color="#718096" />
        <Text fontSize="xs" color="gray.600">
          {label}
        </Text>
      </HStack>
      {children}
    </HStack>
  );
}

function Chip({ colorScheme, children }) {
  return (
    <Badge px={2} py={1} borderRadius="md" fontSize="body.xxs" fontWeight="500" colorScheme={colorScheme}>
      {children}
    </Badge>
  );
}

function NotAssigned() {
  return (
    <Text fontSize="xs" color="gray.400">
      Not assigned
    </Text>
  );
}

/**
 * The stage label and a bar for it.
 *
 * Keyed on `stageType`, never on `stage`: the stage NAME is editable per round, so a layout keyed on it breaks
 * the first time somebody renames a stage. The label is still the name, because that is what the user reads.
 */
function StageBar({ question, large }) {
  const progress = progressOf(question.stageType);
  const color = getProgressColor(question.stageType);
  const dashed = getProgressStyle(question.stageType);
  return (
    <Box minW="140px" maxW={large ? "100%" : "200px"}>
      <Text fontSize="xs" color={large ? "gray.600" : "gray.500"} mb={1}>
        {question.stage}
      </Text>
      <Box
        h={large ? "14px" : "6px"}
        borderRadius={large ? "md" : "full"}
        bg="gray.100"
        overflow="hidden"
      >
        <Box
          h="100%"
          w={`${progress}%`}
          borderRadius={large ? "md" : "full"}
          bg={color}
          sx={dashed}
        />
      </Box>
    </Box>
  );
}
